import React from 'react';
import { ArrowRight, Menu, X } from 'lucide-react';
import { DeepDocsBrand } from './DeepDocsBrand';
import { NAV_LINKS } from './data';

export function SiteNav({
  currentView,
  onNavigate,
  logoUrl,
  isNavScrolled = false,
  headerOffset = 0,
  mobileMenuOpen = false,
  onMobileMenuChange,
  position = 'fixed',
}) {
  const positionClass = position === 'fixed' ? 'fixed left-0 right-0' : 'relative';

  return (
    <>
      <nav
        data-cq-region="nav"
        className={`${positionClass} z-50 border-b transition-[background-color,border-color,box-shadow] duration-300 ${
          isNavScrolled
            ? 'border-white/10 bg-[#060606]/80 shadow-[0_10px_30px_-12px_rgba(3,10,24,0.55)] backdrop-blur-xl'
            : 'border-white/[0.06] bg-transparent'
        }`}
        style={position === 'fixed' ? { top: headerOffset } : undefined}
        aria-label="Primary"
      >
        <div className="mx-auto grid h-16 w-full max-w-[90rem] grid-cols-[1fr_auto] items-center px-8 md:grid-cols-[1fr_auto_1fr] lg:px-12 xl:px-16">
          <div className="justify-self-start">
            <DeepDocsBrand logoUrl={logoUrl} onClick={() => onNavigate('home')} />
          </div>

          <div className="hidden h-16 items-center gap-8 md:flex lg:gap-10">
            {NAV_LINKS.map((link) => {
              const isActive = currentView === link.id;
              return (
                <button
                  key={link.id}
                  type="button"
                  onClick={() => onNavigate(link.id)}
                  aria-current={isActive ? 'page' : undefined}
                  className={`relative flex h-full items-center text-[11px] font-medium uppercase tracking-[0.18em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent/50 ${
                    isActive ? 'text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {link.label}
                  <span
                    aria-hidden="true"
                    className={`absolute inset-x-0 bottom-0 h-[2px] rounded-full bg-brand-accent transition-[opacity,transform] duration-300 ${
                      isActive
                        ? 'opacity-100 shadow-[0_0_8px_rgba(66,165,245,0.6)]'
                        : 'scale-x-0 opacity-0'
                    }`}
                  ></span>
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-self-end gap-1">
            <a
              href="#cq-consult"
              className="group hidden items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:border-brand-accent/60 hover:bg-brand-accent/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent/50 md:inline-flex"
            >
              Initiate Project
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </a>

            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center text-white transition-colors hover:text-brand-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent/50 md:hidden"
              onClick={() => onMobileMenuChange?.(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-nav-menu"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </nav>

      <div
        id="mobile-nav-menu"
        className={`${position === 'fixed' ? 'fixed inset-x-0 bottom-0' : 'absolute inset-x-0 bottom-0'} z-40 flex flex-col items-center justify-center gap-1 bg-[#060606]/95 px-6 backdrop-blur-2xl transition-all duration-300 md:hidden ${
          mobileMenuOpen ? 'visible opacity-100' : 'invisible pointer-events-none opacity-0'
        }`}
        style={position === 'fixed' ? { top: headerOffset } : { top: 0 }}
        aria-hidden={!mobileMenuOpen}
      >
        {[
          { id: 'home', label: 'Home' },
          ...NAV_LINKS,
        ].map((link) => (
          <button
            key={link.id}
            type="button"
            onClick={() => onNavigate(link.id)}
            className={`w-full max-w-sm px-6 py-3 text-center text-xs font-medium uppercase tracking-[0.18em] transition-colors ${
              currentView === link.id ? 'text-white' : 'text-slate-500 hover:text-brand-accent'
            }`}
          >
            {link.label}
          </button>
        ))}
        <a
          href="#cq-consult"
          onClick={() => onMobileMenuChange?.(false)}
          className="mt-8 inline-flex w-full max-w-sm items-center justify-center gap-3 rounded-full bg-white px-8 py-4 text-xs font-bold uppercase tracking-[0.18em] text-black transition-colors hover:bg-slate-200"
        >
          Initiate Project
          <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    </>
  );
}
